import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../../../config';
import { fetchOrgPropertyById } from '../../../hooks/useOrgProperty';
import { parseApiJson } from '../../../lib/api-error';
import type { EnvVarDefinition } from './property-fields';

/** The property fields the settings tabs read and write. */
export type PropertySettingsRecord = {
  id: string;
  name: string;
  description?: string | null;
  defaultAdHocEnvironmentTemplateId?: string | null;
  loginPath?: string | null;
  envVarDefinitions?: EnvVarDefinition[] | null;
  sessionSeeds?: unknown;
};

export function usePropertySettingsQuery(propertyId: string) {
  return useQuery({
    queryKey: ['property', propertyId],
    queryFn: async () => fetchOrgPropertyById<PropertySettingsRecord>(propertyId),
  });
}

interface UsePropertySectionOptions<T> {
  propertyId: string;
  /** Pulls this section's slice out of the loaded property. */
  select: (property: PropertySettingsRecord) => T;
  /** Turns the section's slice into the PUT body. */
  toPatch: (draft: T) => Record<string, unknown>;
}

export interface PropertySection<T> {
  draft: T;
  setDraft: React.Dispatch<React.SetStateAction<T>>;
  isLoading: boolean;
  isDirty: boolean;
  isSaving: boolean;
  save: () => void;
  discard: () => void;
}

/**
 * Owns one settings section's form state: its slice of the property, whether
 * that slice is dirty, and a save that PUTs *only* its own fields.
 *
 * The update route treats an omitted key as "leave unchanged"
 * (`apps/api/src/properties/routes.ts` → `PropertyRepo.update`), which is what
 * lets sections on two different tabs save independently without clobbering
 * each other's fields.
 */
export function usePropertySection<T>({
  propertyId,
  select,
  toPatch,
}: UsePropertySectionOptions<T>): PropertySection<T> {
  const qc = useQueryClient();
  const propertyQ = usePropertySettingsQuery(propertyId);

  const [draft, setDraft] = React.useState<T | null>(null);
  const baselineRef = React.useRef<string | null>(null);
  // Mirrors `draft` so the refetch effect can read the live value without
  // taking it as a dependency (which would re-run the effect on every keystroke).
  const draftRef = React.useRef<T | null>(null);
  draftRef.current = draft;

  // `select`/`toPatch` are defined inline at each call site, so they are new
  // identities every render — key the reset off the loaded data instead.
  const selectRef = React.useRef(select);
  selectRef.current = select;
  const toPatchRef = React.useRef(toPatch);
  toPatchRef.current = toPatch;

  const isDraftDirty = (candidate: T | null) =>
    candidate !== null &&
    baselineRef.current !== null &&
    JSON.stringify(candidate) !== baselineRef.current;

  React.useEffect(() => {
    if (!propertyQ.data) return;
    // Every section on the page reads one shared `['property', id]` query, and
    // any section's save invalidates it. Rebasing unconditionally on the
    // refetch would wipe a sibling section's unsaved edits, so a dirty section
    // keeps its draft (and its baseline, so it still reads as dirty) and only
    // a clean one adopts fresh server state.
    if (isDraftDirty(draftRef.current)) return;
    const next = selectRef.current(propertyQ.data);
    baselineRef.current = JSON.stringify(next);
    setDraft(next);
  }, [propertyQ.data]);

  const save = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const r = await fetch(`${API_URL}/org/properties/${propertyId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      return parseApiJson(r, 'Failed to update application');
    },
    onSuccess: () => {
      // Adopt what we just persisted as the new baseline before the refetch
      // lands: that makes this section clean, so the effect above lets it take
      // the server's copy while dirty siblings hold on to theirs.
      baselineRef.current = JSON.stringify(draftRef.current);
      qc.invalidateQueries({ queryKey: ['property', propertyId] });
      qc.invalidateQueries({ queryKey: ['properties'] });
    },
  });

  const isDirty = isDraftDirty(draft);

  return {
    // Callers render nothing until the query resolves, so the non-null cast
    // here is guarded by `isLoading`.
    draft: (draft ?? ({} as T)) as T,
    setDraft: setDraft as React.Dispatch<React.SetStateAction<T>>,
    isLoading: propertyQ.isLoading || draft === null,
    isDirty,
    isSaving: save.isPending,
    save: () => {
      if (draft === null) return;
      save.mutate(toPatchRef.current(draft));
    },
    discard: () => {
      if (baselineRef.current === null) return;
      setDraft(JSON.parse(baselineRef.current) as T);
    },
  };
}
